import React from "react";
import { TFunction } from "i18next";

export type FidelityValues = {
    designation: string;
    choiceFidelity: string;
    numberFidelityPoints: string;
    moneyCorrespondence: string;
    password: string;
    remark: string;
    login: string;
    user: string;
    dashboardLink: string;
    apiKey: string;
    auresKonnectGroup: string;
    authenticationMode: string[];
    sortedVisibility: SaleModeVisibility[];
};

export type FidelityFlags = {
    isEdited: boolean;
    isCentralizationActive: boolean;
    isVisibiliteActive: boolean;
};

export type FidelityErrors = {
    designation: boolean;
    login: boolean;
    password: boolean;
    user: boolean;
    dashboardLink: boolean;
    apiKey: boolean;
    group: boolean;
    numberFidelityPoints: boolean;
};

export type FidelityFormApi = {
    values: FidelityValues;
    flags: FidelityFlags;
    errors: FidelityErrors;
    setValue: <K extends keyof FidelityValues>(
        key: K,
        value: FidelityValues[K]
    ) => void;
    setFlag: <K extends keyof FidelityFlags>(
        key: K,
        value: FidelityFlags[K]
    ) => void;
    setError: <K extends keyof FidelityErrors>(
        key: K,
        value: FidelityErrors[K]
    ) => void;
};

const INITIAL_VALUES: FidelityValues = {
    designation: "",
    choiceFidelity: "",
    numberFidelityPoints: "",
    moneyCorrespondence: "",
    password: "",
    remark: "",
    login: "",
    user: "",
    dashboardLink: "",
    apiKey: "",
    auresKonnectGroup: "",
    authenticationMode: [],
    sortedVisibility: [],
};

const INITIAL_FLAGS: FidelityFlags = {
    isEdited: false,
    isCentralizationActive: false,
    isVisibiliteActive: false,
};

const INITIAL_ERRORS: FidelityErrors = {
    designation: false,
    login: false,
    password: false,
    user: false,
    dashboardLink: false,
    apiKey: false,
    group: false,
    numberFidelityPoints: false,
};

export function useFidelityForm(
    selectedShopsRows: FidelityRow[],
    t: TFunction
): FidelityFormApi {
    const [values, setValuesState] = React.useState<FidelityValues>(
        INITIAL_VALUES
    );
    const [flags, setFlagsState] = React.useState<FidelityFlags>(INITIAL_FLAGS);
    const [errors, setErrorsState] = React.useState<FidelityErrors>(
        INITIAL_ERRORS
    );

    const setValue = React.useCallback(
        <K extends keyof FidelityValues>(key: K, value: FidelityValues[K]) => {
            setValuesState((prev) => ({ ...prev, [key]: value }));
        },
        []
    );

    const setFlag = React.useCallback(
        <K extends keyof FidelityFlags>(key: K, value: FidelityFlags[K]) => {
            setFlagsState((prev) => ({ ...prev, [key]: value }));
        },
        []
    );

    const setError = React.useCallback(
        <K extends keyof FidelityErrors>(key: K, value: FidelityErrors[K]) => {
            setErrorsState((prev) => ({ ...prev, [key]: value }));
        },
        []
    );

    React.useEffect(() => {
        const row = selectedShopsRows?.[0];
        if (row?.Id === undefined) return;

        setValuesState({
            designation: row[t("Display name")] ?? "",
            choiceFidelity: row[t("Loyalty")] ?? "",
            numberFidelityPoints: row?.number_fidelity_points ?? "",
            moneyCorrespondence: row?.moneyCorrespondence ?? "",
            authenticationMode:
                row?.authentication_mode !== undefined
                    ? row.authentication_mode.split(",")
                    : [],
            sortedVisibility: row?.sortedVisibility ?? [],
            remark: row[t("Remark")] ?? "",
            login: row?.Login ?? "",
            password: row[t("Password")] ?? "",
            user: row.user ?? "",
            auresKonnectGroup: row[t("Group")] ?? "",
            dashboardLink: row[t("Dashboard Link")] ?? "",
            apiKey: row["API key"] ?? "",
        });
        setFlagsState((prev) => ({
            ...prev,
            isCentralizationActive: row[t("isCentralizationActive")] ?? false,
            isVisibiliteActive: row[t("isVisibiliteActive")] ?? false,
        }));
    }, [selectedShopsRows, t]);

    return { values, flags, errors, setValue, setFlag, setError };
}
